import { CloseOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import React from "react";
import styled from "styled-components";

import { QueryCacheKey } from "../../../api";
import {
  Button,
  CenterAlign,
  Drawer,
  FlexColumn,
  LoaderWithPerfTimings,
  Paper,
  PaperContent,
  Typography,
} from "../../../components";
import { useApiContext } from "../../../utils";
import { AlertDetailsSidebarContent } from "../../Dashboard/routes/pages/AlertDetailsSidebarContent";

const StyledHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 56px;
  justify-content: space-between;
  box-shadow: rgba(0, 0, 0, 0.1) 0px 3px 18px 0px;
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};

  .closeButton {
    margin-right: -16px;
  }
`;

type Props = {
  alertId: string | undefined;
  onClose: (value: string | undefined) => void;
};

export const AlertDetailsDrawer: React.FC<React.PropsWithChildren<Props>> = ({
  alertId,
  onClose,
}) => {
  const { apiClient } = useApiContext();

  const {
    data: alertResponse,
    isLoading: isLoadingAlert,
    error: errorAlert,
  } = useQuery([QueryCacheKey.ALERTS, alertId], () => apiClient.getAlert({ alert_id: alertId! }), {
    staleTime: 0,
    cacheTime: 0,
    enabled: Boolean(alertId),
  });

  const alert = alertResponse?.data.alert;

  const renderContent = () => {
    if (isLoadingAlert) {
      return (
        <CenterAlign>
          <LoaderWithPerfTimings name="AlertDetailsDrawer" size="large" />
        </CenterAlign>
      );
    }

    if (errorAlert) {
      return (
        <CenterAlign>
          <Paper>
            <PaperContent>
              <Typography fontWeight="bold" gutterBottom>
                Error
              </Typography>
              <Typography>Unable to load import</Typography>
            </PaperContent>
          </Paper>
        </CenterAlign>
      );
    }

    if (alert === undefined) {
      return <noscript />;
    }

    return <AlertDetailsSidebarContent alert={alert} onClose={onClose} />;
  };

  return (
    <Drawer
      open={alertId !== undefined}
      setOpen={open => {
        if (!open) {
          onClose(undefined);
        }
      }}
      closeOnClickAway={false}
    >
      {renderContent()}
    </Drawer>
  );
};
